import { useEffect, useState } from 'react';
import PMModal from './PMModal';

const API_BASE = process.env.REACT_APP_API_URL || "/api";
function getToken() {
  const role = sessionStorage.getItem("activeRole");
  if (role) { const t = localStorage.getItem("token_"+role); if(t) return t; }
  for (const r of ["pm","superadmin","hr","employee","itadmin","intern"]) { const t = localStorage.getItem("token_"+r); if(t) return t; }
  return null;
}
async function apiCall(path, opts={}) {
  const token = getToken();
  const headers = {...(opts.headers||{}), "Content-Type":"application/json"};
  if(token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, {...opts, headers});
  const text = await res.text();
  let data; try { data = JSON.parse(text); } catch { data = {}; }
  if(!res.ok) throw new Error(data?.error||data?.message||"Request failed");
  return data;
}

const fmtDate = (d) => {
  if(!d) return '—';
  try { return new Date(d).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }); } catch { return d; }
};

// ─── Tab: IT Admin Announcements ───────────────────────────────────────────
function AnnouncementsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await apiCall('/pm/announcements');
        // Only show IT Admin created announcements
        const itAnns = (Array.isArray(data) ? data : []).filter(
          a => a.source_role === 'itadmin' || a.published_by_role === 'itadmin'
        );
        setItems(itAnns);
      } catch { setItems([]); }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div style={{ textAlign:'center', padding:60, color:'#94a3b8' }}>Loading announcements...</div>;

  if (items.length === 0) return (
    <div style={{ textAlign:'center', padding:60, color:'#94a3b8' }}>
      <div style={{ fontSize:48, marginBottom:10 }}>📢</div>
      <div style={{ fontWeight:600, marginBottom:4 }}>No IT Admin announcements yet</div>
      <div style={{ fontSize:13 }}>Announcements from IT Admin will appear here</div>
    </div>
  );

  return (
    <>
      <div style={{ display:'grid', gap:10 }}>
        {items.map((a, i) => (
          <div key={a.id||i} style={{ border:'1px solid #e0e7ff', borderRadius:10, padding:'14px 16px', background:'#f5f3ff', boxShadow:'0 1px 4px rgba(0,0,0,.05)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:700, fontSize:14, color:'#1e293b', marginBottom:2 }}>{a.title}</div>
                <div style={{ fontSize:12, color:'#64748b' }}>
                  <span style={{ background:'#eff6ff', color:'#1d4ed8', padding:'1px 8px', borderRadius:20, fontWeight:700, fontSize:11, marginRight:6 }}>🖥️ IT Admin</span>
                  By {a.created_by || 'IT Admin'} · {fmtDate(a.created_at)}
                </div>
              </div>
              <span style={{ background:'#eef2ff', color:'#4338ca', borderRadius:20, padding:'3px 10px', fontSize:11, fontWeight:700, flexShrink:0, marginLeft:8 }}>
                {a.audience_label || a.audience || 'All'}
              </span>
            </div>
            <div style={{ fontSize:13, color:'#475569', background:'#fff', borderRadius:6, padding:'8px 10px', marginBottom:10, lineHeight:1.6 }}>
              {(a.body||'').substring(0,160)}{(a.body||'').length > 160 ? '…' : ''}
            </div>
            <button onClick={() => setActive(a)}
              style={{ padding:'5px 14px', background:'#4f46e5', color:'#fff', border:'none', borderRadius:6, fontSize:12, cursor:'pointer', fontWeight:600 }}>
              👁 View Full
            </button>
          </div>
        ))}
      </div>

      {/* Announcement detail modal */}
      {active && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.4)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'#fff', borderRadius:14, width:'100%', maxWidth:600, maxHeight:'85vh', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,.2)' }}>
            <div style={{ background:'linear-gradient(135deg,#4f46e5,#7c3aed)', color:'#fff', padding:'14px 18px', borderRadius:'14px 14px 0 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontWeight:700, fontSize:15 }}>📢 IT Admin Announcement</div>
                <div style={{ fontSize:11, opacity:.8, marginTop:2 }}>By {active.created_by || 'IT Admin'} · {fmtDate(active.created_at)}</div>
              </div>
              <button onClick={() => setActive(null)} style={{ background:'rgba(255,255,255,.2)', border:'none', color:'#fff', borderRadius:8, width:30, height:30, cursor:'pointer', fontSize:18 }}>×</button>
            </div>
            <div style={{ padding:18, overflowY:'auto', flex:1 }}>
              <h3 style={{ margin:'0 0 12px', color:'#1e293b' }}>{active.title}</h3>
              {active.audience_label && (
                <div style={{ marginBottom:12, fontSize:12, color:'#64748b' }}>
                  Audience: <strong>{active.audience_label}</strong>
                </div>
              )}
              <div style={{ fontSize:14, color:'#374151', lineHeight:1.7, whiteSpace:'pre-wrap' }}>{active.body}</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Tab: Evaluations ──────────────────────────────────────────────────────
function EvaluationsTab() {
  const [evals, setEvals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiCall('/itadmin/evaluations/pm');
      setEvals(Array.isArray(data) ? data : []);
    } catch { setEvals([]); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const openEval = (e) => { setActive(e); setReply(e.pm_reply || ''); };

  const submitReply = async () => {
    if (!reply.trim()) { alert('Please write a reply'); return; }
    setSubmitting(true);
    try {
      await apiCall('/itadmin/evaluation/reply', { method:'PUT', body: JSON.stringify({ evalId: active.id, reply }) });
      showToast('Reply sent to IT Admin!');
      setActive(null);
      await load();
    } catch(e) { alert(e.message); }
    setSubmitting(false);
  };

  const pending = evals.filter(e => e.status === 'sent');
  const replied = evals.filter(e => e.status !== 'sent');

  if (loading) return <div style={{ textAlign:'center', padding:60, color:'#94a3b8' }}>Loading evaluations...</div>;

  return (
    <>
      {toast && <div style={{ background:'#dcfce7', border:'1px solid #bbf7d0', color:'#15803d', borderRadius:8, padding:'10px 14px', marginBottom:14, fontWeight:600, fontSize:13 }}>✅ {toast}</div>}

      {evals.length === 0 ? (
        <div style={{ textAlign:'center', padding:60, color:'#94a3b8' }}>
          <div style={{ fontSize:48, marginBottom:10 }}>📋</div>
          <div style={{ fontWeight:600, marginBottom:4 }}>No evaluations received yet</div>
          <div style={{ fontSize:13 }}>When IT Admin sends an evaluation for an announcement, it appears here</div>
        </div>
      ) : (
        <>
          {pending.length > 0 && (
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:13, fontWeight:700, color:'#4f46e5', textTransform:'uppercase', letterSpacing:.5, marginBottom:10 }}>⏳ Awaiting Your Reply ({pending.length})</div>
              <div style={{ display:'grid', gap:10 }}>{pending.map(e => <EvalCard key={e.id} e={e} onView={openEval} />)}</div>
            </div>
          )}
          {replied.length > 0 && (
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:'#059669', textTransform:'uppercase', letterSpacing:.5, marginBottom:10 }}>✅ Replied ({replied.length})</div>
              <div style={{ display:'grid', gap:10 }}>{replied.map(e => <EvalCard key={e.id} e={e} onView={openEval} />)}</div>
            </div>
          )}
        </>
      )}

      {/* Eval detail modal */}
      <PMModal open={!!active} onClose={() => setActive(null)} title={`Evaluation — ${active?.ann_title || ''}`}>
        {active && (
          <div>
            <div style={{ background:'#f8fafc', borderRadius:8, padding:'10px 14px', marginBottom:14 }}>
              <div style={{ fontSize:11, color:'#94a3b8', textTransform:'uppercase', marginBottom:4 }}>Announcement</div>
              <div style={{ fontWeight:700, color:'#1e293b', marginBottom:4 }}>{active.ann_title}</div>
              <div style={{ fontSize:12, color:'#64748b', lineHeight:1.5 }}>{(active.ann_body||'').substring(0,150)}</div>
            </div>
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#374151', marginBottom:6 }}>💬 IT Admin ({active.evaluator_name}) says:</div>
              <div style={{ background:'#eef2ff', border:'1px solid #c7d2fe', borderRadius:8, padding:'10px 12px', fontSize:13, color:'#3730a3', lineHeight:1.6 }}>
                {active.comment}
              </div>
              <div style={{ fontSize:11, color:'#94a3b8', marginTop:4 }}>{fmtDate(active.created_at)}</div>
            </div>
            <div style={{ marginBottom:10 }}>
              <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:6 }}>Your Reply *</label>
              <textarea rows={4} value={reply} onChange={e => setReply(e.target.value)}
                placeholder="Write your response to the IT Admin's evaluation..."
                style={{ width:'100%', padding:'8px 10px', border:'1px solid #d1d5db', borderRadius:8, fontSize:13, resize:'vertical', boxSizing:'border-box' }}
                disabled={active.status === 'pm_replied'} />
            </div>
            {active.status === 'pm_replied' ? (
              <div style={{ background:'#dcfce7', borderRadius:8, padding:'10px 12px', fontSize:12, color:'#15803d', fontWeight:500 }}>
                ✅ Reply already sent to IT Admin.
              </div>
            ) : (
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                <button onClick={() => setActive(null)} style={{ padding:'8px 16px', border:'1px solid #e2e8f0', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13 }}>Close</button>
                <button onClick={submitReply} disabled={submitting}
                  style={{ padding:'8px 18px', background:'#4f46e5', color:'#fff', border:'none', borderRadius:8, fontWeight:700, cursor:'pointer', fontSize:13 }}>
                  {submitting ? 'Sending...' : '📤 Send Reply'}
                </button>
              </div>
            )}
          </div>
        )}
      </PMModal>
    </>
  );
}

function EvalCard({ e, onView }) {
  const isPending = e.status === 'sent';
  return (
    <div style={{ border:`1px solid ${isPending ? '#c7d2fe' : '#e2e8f0'}`, borderRadius:10, padding:'14px 16px', background:isPending ? '#f5f3ff' : '#fff', boxShadow:'0 1px 4px rgba(0,0,0,.05)' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:14, color:'#1e293b' }}>{e.ann_title}</div>
          <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>From: {e.evaluator_name} (IT Admin) · {fmtDate(e.created_at)}</div>
        </div>
        <span style={{ background:isPending?'#eef2ff':'#dcfce7', color:isPending?'#4f46e5':'#16a34a', borderRadius:20, padding:'3px 10px', fontSize:11, fontWeight:700 }}>
          {isPending ? '⏳ Awaiting Reply' : '✅ Replied'}
        </span>
      </div>
      <div style={{ fontSize:12, color:'#475569', background:'#f8fafc', borderRadius:6, padding:'8px 10px', marginBottom:10, lineHeight:1.5 }}>
        {(e.comment||'').substring(0,120)}{(e.comment||'').length > 120 ? '...' : ''}
      </div>
      <div style={{ display:'flex', justifyContent:'flex-end' }}>
        <button onClick={() => onView(e)} style={{ padding:'5px 14px', background:isPending?'#4f46e5':'#f1f5f9', color:isPending?'#fff':'#374151', border:'none', borderRadius:6, fontSize:12, cursor:'pointer', fontWeight:600 }}>
          {isPending ? '📝 View & Reply' : '👁 View'}
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────
function PMEvalsPg() {
  const [tab, setTab] = useState('announcements');

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom:20 }}>
        <h2 style={{ margin:0, color:'#1e293b', fontSize:20, fontWeight:700 }}>🖥️ IT Admin — Announcements & Evaluations</h2>
        <p style={{ margin:'4px 0 0', color:'#64748b', fontSize:13 }}>View IT Admin announcements and respond to evaluations</p>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:20, background:'#f1f5f9', padding:4, borderRadius:10, width:'fit-content' }}>
        {[
          { k:'announcements', l:'📢 Announcements' },
          { k:'evaluations',   l:'📋 Evaluations'  },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            style={{ padding:'8px 18px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:700, transition:'all 0.15s',
              background: tab === t.k ? '#fff' : 'transparent',
              color:       tab === t.k ? '#4f46e5' : '#64748b',
              boxShadow:   tab === t.k ? '0 1px 4px rgba(0,0,0,.1)' : 'none' }}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === 'announcements' ? <AnnouncementsTab /> : <EvaluationsTab />}
    </div>
  );
}

export default PMEvalsPg;
